import { I18n } from '@calendly/i18n'
import { BodyText } from '@calendly/ui/components/typography'

import { RECAP_ACCESS_LEVELS, RecapPermission } from 'features/recaps/constants'
import { useRecapShare } from 'features/recaps/meeting_list/meeting_card/recap_share_context'

import { USER_ACCESS_LIST_VARIANT } from './constants'
import { GroupAccessListItem } from './group_access_list_item'
import { OrganizationAccessListItem } from './organization_access_list_item'
import {
  AccessList as AccessListContainer,
  AccessListSectionContainer,
  AccessListSectionsContainer,
  accessListSectionHeaderCss,
} from './styles'
import { UserAccessListItem } from './user_access_list_item'

const AccessListSection = ({
  title,
  children,
  testId,
}) => (
  <AccessListSectionContainer data-testid={testId}>
    {title ? (
      <BodyText weight="bold" size="large" className={accessListSectionHeaderCss}>
        {title}
      </BodyText>
    ) : null}
    <AccessListContainer>
      {children}
    </AccessListContainer>
  </AccessListSectionContainer>
)

const buildCurrentUserRow = (currentUser) => ({
  uuid: currentUser.uuid,
  name: currentUser.profile?.name ?? currentUser.name ?? '',
  email: currentUser.email ?? '',
  avatarUrl: currentUser.avatar_url ?? null,
  avatarLetter: currentUser.avatar_letter ?? null,
  isCurrentUser: true,
})

export const AccessList = () => {
  const {
    recapPermission,
    currentUser,
    accessLevel: { value: accessLevel },
    accessList: {
      owner,
      internalUsers,
      userGroups,
      externalUsers,
      pendingInvites,
      organization,
      isLoading,
    },
    permissionUpdates: {
      getUserRelation,
      getUserGroupRelation,
      getUserEmailRelation,
    },
  } = useRecapShare()

  if (isLoading) {
    return null
  }

  const isRestricted = accessLevel === RECAP_ACCESS_LEVELS.RESTRICTED
  const showSharerSelfRow = isRestricted && recapPermission === RecapPermission.SHARE && currentUser

  const visibleUsers = internalUsers.filter((user) => {
    const relation = getUserRelation(user.uuid, user.relation)
    return relation !== null
  })

  const grantUsers = showSharerSelfRow
    ? visibleUsers.filter((user) => user.uuid !== currentUser.uuid)
    : visibleUsers

  const visibleUserGroups = userGroups.filter((userGroup) => {
    const relation = getUserGroupRelation(userGroup.uuid, userGroup.relation)
    return relation !== null
  })

  const visibleExternalUsers = (externalUsers ?? []).filter((user) => (
    getUserEmailRelation(user.email, user.relation) !== null
  ))

  const visiblePendingInvites = (pendingInvites ?? []).filter((invite) => (
    getUserEmailRelation(invite.email, invite.relation) !== null
  ))

  const showGrantedEntries = isRestricted && (
    grantUsers.length > 0
    || visibleUserGroups.length > 0
    || visibleExternalUsers.length > 0
    || visiblePendingInvites.length > 0
  )

  return (
    <AccessListSectionsContainer>
      <AccessListSection testId="access-list-people-section">
        {owner ? (
          <UserAccessListItem
            user={owner}
            testId="access-list-owner"
            variant={USER_ACCESS_LIST_VARIANT.OWNER}
          />
        ) : null}
        {showSharerSelfRow ? (
          <UserAccessListItem
            user={buildCurrentUserRow(currentUser)}
            testId="access-list-current-user"
            variant={USER_ACCESS_LIST_VARIANT.CURRENT_SHARER}
          />
        ) : null}
        {showGrantedEntries && (
          <>
            {grantUsers.map((user) => (
              <UserAccessListItem
                key={user.uuid}
                user={user}
                testId={`access-list-user-${user.uuid}`}
                variant={USER_ACCESS_LIST_VARIANT.GRANT}
              />
            ))}
            {visibleUserGroups.map((userGroup) => (
              <GroupAccessListItem
                key={userGroup.uuid}
                userGroup={userGroup}
              />
            ))}
            {visibleExternalUsers.map((user) => (
              <UserAccessListItem
                key={user.uuid}
                user={user}
                testId={`access-list-external-${user.uuid}`}
                variant={USER_ACCESS_LIST_VARIANT.EXTERNAL}
              />
            ))}
            {visiblePendingInvites.map((invite) => (
              <UserAccessListItem
                key={`pending-${invite.email}`}
                user={invite}
                testId={`access-list-pending-${invite.email}`}
                variant={USER_ACCESS_LIST_VARIANT.PENDING_INVITE}
              />
            ))}
          </>
        )}
      </AccessListSection>
      {organization ? (
        <AccessListSection
          title={I18n.t('publisher.meeting_recap.share_modal.access_list.general_access.header')}
          testId="access-list-general-access-section"
        >
          <OrganizationAccessListItem />
        </AccessListSection>
      ) : null}
    </AccessListSectionsContainer>
  )
}
